import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  MCP_OAUTH2_PROVIDER,
  OAUTH2_PROVIDER,
} from "@arcadeai/design-system/metadata/oauth-providers";
import { findPrebuiltProviderById } from "@arcadeai/design-system/metadata/utils";
import { Search, X } from "lucide-react";
import { useCallback, useMemo } from "react";
import type { ConnectedAccountItem } from "@/app/oauth/types";
import { useDataTable } from "@/components/custom/table/data-table";
import type { DataTableFacetedFilterOption } from "@/components/custom/table/data-table-faceted-filter";
import { DataTableFacetedFilter } from "@/components/custom/table/data-table-faceted-filter";
import { DataTableViewOptions } from "@/components/custom/table/data-table-view-options";

export function AccountsTableToolbar() {
  const table = useDataTable();
  const isFiltered = table.getState().columnFilters.length > 0;

  // Get current filter values
  const userIdFilter =
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    (table.getColumn("user_id")?.getFilterValue() as string[]) ?? [];
  const providerIdFilter =
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    (table.getColumn("provider_id")?.getFilterValue() as string[]) ?? [];

  // Generate filter options from table data
  const filterOptions = useMemo(() => {
    const data = table
      .getFilteredRowModel()
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      .rows.map((row) => row.original as ConnectedAccountItem);

    // Get unique user IDs
    const uniqueUserIds = Array.from(
      new Set(data.map((account) => account.user_id))
    );
    const userIdOptions: DataTableFacetedFilterOption[] = uniqueUserIds.map(
      (userId) => ({
        label: userId,
        value: userId,
      })
    );

    // Get unique provider IDs with their display names and icons
    const uniqueProviderIds = Array.from(
      new Set(data.map((account) => account.provider_id))
    );
    const providerIdOptions: DataTableFacetedFilterOption[] =
      uniqueProviderIds.map((providerId) => {
        const isMcp = data.some(
          (account) =>
            account.provider_id === providerId &&
            account.provider_type === "mcp_oauth2"
        );

        const descriptor = isMcp
          ? MCP_OAUTH2_PROVIDER
          : (findPrebuiltProviderById(providerId) ?? OAUTH2_PROVIDER);

        return {
          label: isMcp ? providerId : descriptor.name || providerId,
          value: providerId,
          icon: descriptor.icon,
        };
      });

    return {
      userIdOptions: userIdOptions.sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
      providerIdOptions: providerIdOptions.sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    };
  }, [table]);

  const handleUserIdFilterChange = useCallback(
    (selectedValues: string[]) => {
      table
        .getColumn("user_id")
        ?.setFilterValue(selectedValues.length ? selectedValues : undefined);
    },
    [table]
  );

  const handleProviderIdFilterChange = useCallback(
    (selectedValues: string[]) => {
      table
        .getColumn("provider_id")
        ?.setFilterValue(selectedValues.length ? selectedValues : undefined);
    },
    [table]
  );

  return (
    <div className="flex flex-col items-start justify-between gap-2 lg:flex-row lg:items-center">
      <div className="flex w-full flex-col items-start gap-2 lg:flex-1 lg:flex-row lg:items-center">
        <div className="relative w-full max-w-[250px] lg:w-[200px] xl:w-[250px]">
          <Search className="absolute top-2 left-2 h-4 w-4 text-muted-foreground" />
          <Input
            className="h-8 pl-8 text-sm"
            onChange={(event) =>
              table.getColumn("name")?.setFilterValue(event.target.value)
            }
            placeholder="Search accounts..."
            // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
            value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
          />
        </div>
        <div className="flex w-full flex-wrap gap-2 lg:w-auto">
          {table.getColumn("user_id") &&
            filterOptions.userIdOptions.length > 0 && (
              <DataTableFacetedFilter
                column={table.getColumn("user_id")}
                onSelectionChange={handleUserIdFilterChange}
                options={filterOptions.userIdOptions}
                selectedValues={userIdFilter}
                title="User ID"
              />
            )}

          {table.getColumn("provider_id") &&
            filterOptions.providerIdOptions.length > 0 && (
              <DataTableFacetedFilter
                column={table.getColumn("provider_id")}
                onSelectionChange={handleProviderIdFilterChange}
                options={filterOptions.providerIdOptions}
                selectedValues={providerIdFilter}
                title="Provider"
              />
            )}

          {!!isFiltered && (
            <Button
              className="h-8 px-2 lg:px-3"
              onClick={() => table.resetColumnFilters()}
              variant="warning"
            >
              Reset
              <X className="ml-1 size-4" />
            </Button>
          )}
        </div>
      </div>
      <DataTableViewOptions table={table} />
    </div>
  );
}
